import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '@myua/kit';
import {
  Aspect,
  MagicRef,
  MagicResult,
  MagicPrimaryAsset,
  Resolution,
  assetImageUrl,
  assetThumbUrl,
  refName,
} from '../model';
import { dataUrlToBlob } from '../localOps/download';
import { SavedAssetLink } from '../saveToAssets/types';
import { createHistoryRepository } from './repository';
import { MagicGeneration, PersistedRef } from './types';

// A reference as shown on a history card / used to rebuild a session ref.
// `thumbUrl` / `previewUrl` are display-only; `asset` / `blob` carry what "Reuse
// setup" needs to recreate the original MagicRef.
export type HistoryRefView = {
  key: string;
  kind: 'asset' | 'upload';
  name: string;
  thumbUrl: string | null;
  // The largest render we can show — the original / preview for an Asset Lab
  // ref, the stored bytes for an upload. Feeds the full-size lightbox, where the
  // card's tiny thumbnail would be a blurry mess.
  previewUrl: string | null;
  asset?: MagicPrimaryAsset;
  blob?: Blob;
};

// A stored generation hydrated for the UI: blobs turned into displayable URLs.
export type MagicGenerationView = {
  id: string;
  createdAt: number;
  imageUrl: string | null;
  imageBlob: Blob | null;
  seed: number;
  aspect: Aspect;
  resolution: Resolution | null;
  format: string;
  bytes: number | null;
  source?: 'removeBg' | 'upscale';
  model: string;
  prompt: string;
  templateId: string;
  templateLabel: string;
  refs: HistoryRefView[];
  // Set once saved into the Asset Lab — drives the cloud-linked pictogram and
  // the "Reveal in Asset Lab" action. Null while local-only.
  savedAsset: SavedAssetLink | null;
};

// What the studio hands the hook on each produced result. The hook fills in id /
// timestamp / userId and converts the result + upload refs to stored Blobs.
export type NewGenerationInput = {
  result: MagicResult;
  resolution: Resolution | null;
  model: string;
  prompt: string;
  templateId: string;
  templateLabel: string;
  refs: MagicRef[];
};

// Persisted-ref → card/reuse view. Upload thumbs become tracked object URLs;
// asset thumbs reuse the catalog's authenticated thumbnail URL (not tracked —
// it isn't an object URL we own).
function hydrateRef(ref: PersistedRef, track: (url: string) => string): HistoryRefView {
  if (ref.kind === 'asset') {
    return {
      key: ref.key,
      kind: 'asset',
      name: ref.asset.name,
      thumbUrl: assetThumbUrl(ref.asset),
      previewUrl: assetImageUrl(ref.asset),
      asset: ref.asset,
    };
  }
  // One object URL serves both sizes — it already is the full image.
  const url = track(URL.createObjectURL(ref.blob));
  return {
    key: ref.key,
    kind: 'upload',
    name: ref.name,
    thumbUrl: url,
    previewUrl: url,
    blob: ref.blob,
  };
}

function hydrate(record: MagicGeneration, track: (url: string) => string): MagicGenerationView {
  return {
    id: record.id,
    createdAt: record.createdAt,
    imageUrl: record.image ? track(URL.createObjectURL(record.image)) : null,
    imageBlob: record.image,
    seed: record.seed,
    aspect: record.aspect,
    resolution: record.resolution,
    format: record.format,
    bytes: record.bytes,
    source: record.source,
    model: record.model,
    prompt: record.prompt,
    templateId: record.templateId,
    templateLabel: record.templateLabel,
    refs: record.refs.map((r) => hydrateRef(r, track)),
    savedAsset: record.savedAsset ?? null,
  };
}

// The display object URLs a view created (its own — asset thumbs are excluded).
function viewObjectUrls(view: MagicGenerationView): string[] {
  const urls = view.imageUrl ? [view.imageUrl] : [];
  view.refs.forEach((r) => {
    if (r.kind === 'upload' && r.thumbUrl) urls.push(r.thumbUrl);
  });
  return urls;
}

// Generation history backed by the repository (IndexedDB today). Loads the
// logged-in user's saved generations while the studio is open, exposes
// auto-save / delete / clear, and owns the object-URL lifecycle for the blobs
// it hydrates — revoking on delete, clear and close so nothing leaks.
export function useMagicHistory(userId: string, { enabled }: { enabled: boolean }) {
  const [history, setHistory] = useState<MagicGenerationView[]>([]);
  const [loading, setLoading] = useState(false);
  const repository = useRef(createHistoryRepository()).current;
  // Every display object URL we've created, so we can revoke them all on close.
  const urlsRef = useRef<Set<string>>(new Set());

  const track = useCallback((url: string) => {
    urlsRef.current.add(url);
    return url;
  }, []);

  const revoke = useCallback((urls: string[]) => {
    urls.forEach((url) => {
      if (urlsRef.current.has(url)) {
        URL.revokeObjectURL(url);
        urlsRef.current.delete(url);
      }
    });
  }, []);

  // Load (and hydrate) on open; revoke everything and reset on close / unmount.
  useEffect(() => {
    if (!enabled) return undefined;
    // Stable for the component's lifetime (we never reassign .current), so this
    // is the same Set add/delete operate on — safe to use in the cleanup.
    const urls = urlsRef.current;
    let cancelled = false;
    setLoading(true);
    void repository
      .list(userId)
      .then((records) => {
        if (cancelled) return;
        setHistory(records.map((r) => hydrate(r, track)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
      setHistory([]);
    };
  }, [enabled, userId, repository, track]);

  // Persist one produced result, then prepend it to the visible list.
  const addGeneration = useCallback(
    async (input: NewGenerationInput) => {
      const { result } = input;
      if (!result.imageUrl) return;
      try {
        const image = await dataUrlToBlob(result.imageUrl);
        const refs: PersistedRef[] = await Promise.all(
          input.refs.map(async (ref): Promise<PersistedRef> => {
            if (ref.kind === 'asset') {
              return { kind: 'asset', key: ref.key, asset: ref.asset };
            }
            const blob = await dataUrlToBlob(ref.url);
            return { kind: 'upload', key: ref.key, name: refName(ref), blob };
          }),
        );
        const record: MagicGeneration = {
          id: result.id,
          userId,
          createdAt: Date.now(),
          image,
          seed: result.seed,
          aspect: result.aspect,
          resolution: input.resolution,
          format: result.format,
          bytes: result.bytes ?? null,
          source: result.source,
          usage: result.usage ?? null,
          model: input.model,
          prompt: input.prompt,
          templateId: input.templateId,
          templateLabel: input.templateLabel,
          refs,
        };
        await repository.add(record);
        setHistory((prev) => [hydrate(record, track), ...prev.filter((v) => v.id !== record.id)]);
      } catch (err) {
        // Saving is best-effort — surface quota / storage failures quietly so
        // the generation itself (already shown) isn't disrupted.
        void message.warning(
          err instanceof DOMException && err.name === 'QuotaExceededError'
            ? 'History storage is full — this generation was not saved.'
            : 'Could not save this generation to History.',
        );
      }
    },
    [repository, track, userId],
  );

  // Record that a generation has been saved into the Asset Lab: persist the
  // link on its record and reflect it in the visible list so the card's
  // pictogram flips to "linked". Best-effort and a no-op if the record isn't in
  // history (e.g. history disabled / it was never saved locally).
  const markGenerationSaved = useCallback(
    (id: string, link: SavedAssetLink) => {
      setHistory((prev) => prev.map((v) => (v.id === id ? { ...v, savedAsset: link } : v)));
      void repository.update(id, { savedAsset: link });
    },
    [repository],
  );

  const deleteGeneration = useCallback(
    (id: string) => {
      setHistory((prev) => {
        const target = prev.find((v) => v.id === id);
        if (target) revoke(viewObjectUrls(target));
        return prev.filter((v) => v.id !== id);
      });
      void repository.remove(id);
    },
    [repository, revoke],
  );

  const clearHistory = useCallback(() => {
    setHistory((prev) => {
      prev.forEach((v) => revoke(viewObjectUrls(v)));
      return [];
    });
    void repository.clear(userId);
  }, [repository, revoke, userId]);

  return {
    history,
    loading,
    addGeneration,
    markGenerationSaved,
    deleteGeneration,
    clearHistory,
  };
}
